import { useMemo, useState } from "react"
import type { CombinedOrderReport, MeeshoCombinedReport } from "@/features/profit-calculators/calculator-shared/types"
import { getOrderStatuses, SKU_CATEGORIES } from "@/features/profit-calculators/calculator-shared/utils/reportViewUtils"

export const useReportViewState = (report: MeeshoCombinedReport, platform: "meesho" | "amazon") => {
  const orderStatuses = useMemo(() => getOrderStatuses(platform), [platform])
  const [skuTab, setSkuTab] = useState(0)
  const [orderTab, setOrderTab] = useState(0)
  const [skuSearch, setSkuSearch] = useState("")
  const [orderSearch, setOrderSearch] = useState("")
  const [showDetailedLedger, setShowDetailedLedger] = useState(false)
  const [skuPage, setSkuPage] = useState(1)
  const [skuRowsPerPage, setSkuRowsPerPage] = useState(10)
  const [orderPage, setOrderPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedSkuOrders, setSelectedSkuOrders] = useState<CombinedOrderReport[] | null>(null)
  const [selectedSkuName, setSelectedSkuName] = useState("")
  const [breakdownOrder, setBreakdownOrder] = useState<CombinedOrderReport | null>(null)
  const [amazonBreakdownOrder, setAmazonBreakdownOrder] = useState<CombinedOrderReport | null>(null)

  const filteredSkuReports = useMemo(() => {
    const search = skuSearch.toLowerCase()
    const activeCategory = SKU_CATEGORIES[skuTab]
    return report.skuReports.filter(
      item =>
        (item.sku.toLowerCase().includes(search) || item.productName.toLowerCase().includes(search)) &&
        (activeCategory === "ALL" || item.category === activeCategory)
    )
  }, [report.skuReports, skuSearch, skuTab])

  const skuCategoryCounts = useMemo(() => {
    const counts = {
      ALL: report.skuReports.length,
      HIGH: 0,
      MEDIUM: 0,
      LOW: 0,
      LOSS: 0
    }
    report.skuReports.forEach(item => counts[item.category]++)
    return counts
  }, [report.skuReports])

  const paginatedSkuReports = useMemo(() => {
    const start = (skuPage - 1) * skuRowsPerPage
    return filteredSkuReports.slice(start, start + skuRowsPerPage)
  }, [filteredSkuReports, skuPage, skuRowsPerPage])

  const filteredOrders = useMemo(() => {
    const search = orderSearch.toLowerCase()
    const activeStatus = orderStatuses[orderTab]
    return report.orders.filter(
      order =>
        (order.subOrderNo.toLowerCase().includes(search) ||
          order.sku.toLowerCase().includes(search) ||
          order.productName.toLowerCase().includes(search)) &&
        (activeStatus === "ALL" || order.liveOrderStatus === activeStatus)
    )
  }, [report.orders, orderSearch, orderStatuses, orderTab])

  const orderSummaryMetrics = useMemo(() => {
    const totals = filteredOrders.reduce(
      (result, order) => ({
        count: result.count + 1,
        settlement: result.settlement + order.settlementAmount,
        purchase: result.purchase + order.cogs
      }),
      { count: 0, settlement: 0, purchase: 0 }
    )
    const profit = totals.settlement - totals.purchase
    return {
      ...totals,
      profit,
      avgProfit: totals.count ? profit / totals.count : 0
    }
  }, [filteredOrders])

  const paginatedOrders = useMemo(() => {
    const start = (orderPage - 1) * rowsPerPage
    return filteredOrders.slice(start, start + rowsPerPage)
  }, [filteredOrders, orderPage, rowsPerPage])

  const viewSkuDetails = (sku: string, productName: string) => {
    setSelectedSkuOrders(report.orders.filter(order => order.sku === sku))
    setSelectedSkuName(productName)
  }

  return {
    orderStatuses,
    skuTab,
    setSkuTab,
    orderTab,
    setOrderTab,
    skuSearch,
    setSkuSearch,
    orderSearch,
    setOrderSearch,
    showDetailedLedger,
    setShowDetailedLedger,
    skuPage,
    setSkuPage,
    skuRowsPerPage,
    setSkuRowsPerPage,
    orderPage,
    setOrderPage,
    rowsPerPage,
    setRowsPerPage,
    selectedSkuOrders,
    setSelectedSkuOrders,
    selectedSkuName,
    breakdownOrder,
    setBreakdownOrder,
    amazonBreakdownOrder,
    setAmazonBreakdownOrder,
    filteredSkuReports,
    skuCategoryCounts,
    paginatedSkuReports,
    totalSkuPages: Math.ceil(filteredSkuReports.length / skuRowsPerPage),
    filteredOrders,
    orderSummaryMetrics,
    paginatedOrders,
    totalOrderPages: Math.ceil(filteredOrders.length / rowsPerPage),
    viewSkuDetails
  }
}
